import type { JSX } from 'react';
import type { AiChatMessage, AiUndoConflict } from '../../hooks/useAiChat';
import { AiUndoConflictDialog } from './AiUndoConflictDialog';
import { isLive } from './AiTurnCard';

/** The conflict dialog of a chat (AU-7): what was edited, in words, from the card the undo came from. */
export function AiUndoConflictHost({ conflict, messages, onResolve }: {
  readonly conflict: AiUndoConflict | null | undefined;
  readonly messages: readonly AiChatMessage[];
  readonly onResolve: (choice: 'restore' | 'keep-mine' | 'cancel') => void;
}): JSX.Element {
  const turn = conflict == null ? undefined : messages.find((m) => m.role === 'changes' && m.turnData?.turnId === conflict.turnId)?.turnData;
  const conflicted = new Set(conflict?.conflicts ?? []);
  const live = turn === undefined ? [] : turn.changes.filter(isLive);
  // What the undo takes: the asked changes AND what depends on them (the server adds those).
  const goes = new Set(conflict?.changeIds ?? live.map((c) => c.id));
  for (let grew = true; grew;) {
    grew = false;
    for (const c of live) if (!goes.has(c.id) && c.dependsOn !== undefined && goes.has(c.dependsOn)) { goes.add(c.id); grew = true; }
  }
  const selected = live.filter((c) => goes.has(c.id));
  // The list says it all: the GM's edits first, then what else goes with them.
  const items = [
    ...selected.filter((c) => conflicted.has(c.id)).map((c) => c.entity.name),
    ...selected.filter((c) => !conflicted.has(c.id)).map((c) => c.entity.name),
  ].filter((n) => n !== '');
  return (
    <AiUndoConflictDialog
      open={conflict != null}
      items={items}
      hasRest={selected.some((c) => !conflicted.has(c.id))}
      onRestore={(): void => onResolve('restore')}
      onKeepMine={(): void => onResolve('keep-mine')}
      onCancel={(): void => onResolve('cancel')}
    />
  );
}
